import { useAuth } from "../../context/authContext";
import useWorkerStats from "../../hooks/useWorkerStats";
import LoadingState from "../ui/LoadingState";
import GlassCard from "../ui/GlassCard";

const StatBlock = ({ label, value, sublabel, accent = "amber" }) => (
  <div className="group relative rounded-xl px-4 py-3 -mx-4 -my-3 transition-all duration-300 hover:bg-white/[0.04] cursor-default">
    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-white/40">
      {label}
    </p>
    <p className="mt-1.5 text-3xl font-bold text-amber-500 dark:text-amber-400 transition-transform duration-300 group-hover:scale-[1.04] origin-left">
      {value}
    </p>
    {sublabel && (
      <p className="mt-0.5 text-[11px] text-slate-400 dark:text-white/30">
        {sublabel}
      </p>
    )}
  </div>
);

const Stars = ({ score, max = 5 }) => (
  <div className="flex items-center gap-1 mt-2">
    {Array.from({ length: max }).map((_, i) => (
      <svg
        key={i}
        viewBox="0 0 20 20"
        className={`w-4 h-4 transition-all duration-300 ${
          i < Math.round(score)
            ? "fill-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.5)]"
            : "fill-none stroke-slate-500 dark:stroke-white/20"
        }`}
        strokeWidth={i < Math.round(score) ? 0 : 1.5}
      >
        <path d="M10 1.5l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6L1.3 7.7l6.1-.6L10 1.5z" />
      </svg>
    ))}
    {/* <span className="ml-1.5 text-xs font-semibold text-slate-500 dark:text-white/40">
      {score.toFixed(1)}
    </span> */}
  </div>
);

const RatingBlock = ({ label, score }) => (
  <div className="group relative rounded-xl px-4 py-3 -mx-4 -my-3 transition-all duration-300 hover:bg-white/[0.04] cursor-default">
    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-white/40">
      {label}
    </p>
    <p className="mt-1.5 text-3xl font-bold text-amber-500 dark:text-amber-400 transition-transform duration-300 group-hover:scale-[1.04] origin-left">
      {score.toFixed(1)}
    </p>
    <Stars score={score} max={5} />
    <div className="mt-2.5 h-1 w-full rounded-full bg-slate-200/60 dark:bg-white/10 overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-700 ease-out"
        style={{ width: `${(score / 5) * 100}%` }}
      />
    </div>
  </div>
);

const WorkerStats = () => {
  const { code } = useAuth();
  const { stats, loading, error } = useWorkerStats(code);

  const workerStats = {
    numberOfWorkers: stats?.totalResponses ?? 0,
    avgJobSatisfaction: stats?.avgJobSatisfaction ?? 0,
    workLifeBalance: stats?.avgWorkLifeBalanceOutOf5 ?? 0,
    maleCount: stats?.genderCount?.Male ?? 0,
    femaleCount: stats?.genderCount?.Female ?? 0,
  };

  const total = workerStats.maleCount + workerStats.femaleCount;
  const malePct =
    total > 0 ? Math.round((workerStats.maleCount / total) * 100) : 0;
  const femalePct =
    total > 0 ? Math.round((workerStats.femaleCount / total) * 100) : 0;

  return (
    <GlassCard
      accent="violet"
      className="flex flex-col gap-6 text-slate-900 dark:text-white"
    >
      {/* Header */}
      <div className="w-full flex flex-row items-start justify-between">
        <h1 className="font-['Space_Grotesk',_sans-serif] text-[22px] leading-tight font-semibold flex flex-col">
          Worker Insights
          <span className="text-[12px] font-normal text-slate-400 dark:text-white/40 mt-0.5">
            Onboarding overview
          </span>
        </h1>
      </div>

      {loading && <LoadingState rows={3} />}

      {!loading && (
        <>
          {/* Top metrics row */}
          <div className="grid grid-cols-3 gap-x-6 gap-y-8 pt-1">
            <StatBlock
              label="Number of Workers"
              value={workerStats.numberOfWorkers.toLocaleString()}
              sublabel="Total onboarded"
            />
            <RatingBlock
              label="Average Job Satisfaction"
              score={workerStats.avgJobSatisfaction}
            />
            <RatingBlock
              label="Work-Life Balance"
              score={workerStats.workLifeBalance}
            />
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-300/20 dark:via-white/10 to-transparent" />

          {/* Gender split */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-white/40">
                Gender Distribution
              </p>
              <p className="text-[11px] text-slate-400 dark:text-white/30">
                {total.toLocaleString()} total
              </p>
            </div>

            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-200/60 dark:bg-white/10">
              <div
                className="h-full bg-gradient-to-r from-sky-500 to-sky-400 transition-all duration-700 ease-out"
                style={{ width: `${malePct}%` }}
              />
              <div
                className="h-full bg-gradient-to-r from-fuchsia-500 to-fuchsia-400 transition-all duration-700 ease-out"
                style={{ width: `${femalePct}%` }}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="group flex items-center gap-3 rounded-xl border border-slate-200/10 dark:border-white/[0.06] px-3 py-2.5 transition-all duration-300 hover:border-sky-400/30 hover:bg-sky-400/[0.04]">
                <span className="h-2.5 w-2.5 rounded-full bg-sky-400 shrink-0" />
                <div>
                  <p className="text-[11px] text-slate-400 dark:text-white/40">
                    Male
                  </p>
                  <p className="text-lg font-bold leading-tight">
                    {workerStats.maleCount.toLocaleString()}
                    <span className="ml-1.5 text-xs font-medium text-slate-400 dark:text-white/30">
                      {malePct}%
                    </span>
                  </p>
                </div>
              </div>
              <div className="group flex items-center gap-3 rounded-xl border border-slate-200/10 dark:border-white/[0.06] px-3 py-2.5 transition-all duration-300 hover:border-fuchsia-400/30 hover:bg-fuchsia-400/[0.04]">
                <span className="h-2.5 w-2.5 rounded-full bg-fuchsia-400 shrink-0" />
                <div>
                  <p className="text-[11px] text-slate-400 dark:text-white/40">
                    Female
                  </p>
                  <p className="text-lg font-bold leading-tight">
                    {workerStats.femaleCount.toLocaleString()}
                    <span className="ml-1.5 text-xs font-medium text-slate-400 dark:text-white/30">
                      {femalePct}%
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && error && (
        <p className="text-xs text-slate-400 dark:text-white/40 pt-1 border-t border-slate-200/10">
          Showing sample data.
        </p>
      )}
    </GlassCard>
  );
};

export default WorkerStats;
